import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  Typography,
  Tooltip,
  Paper,
  Button,
} from "@mui/material";

import { MdOutlineMail, MdOutlineLocalPhone, MdVerified } from "react-icons/md";
import { AiOutlineLinkedin } from "react-icons/ai";
import { SiWhatsapp } from "react-icons/si";
import { RxCrossCircled } from "react-icons/rx";

import Slider from "react-slick";
import VideoModal from "./VideoModal";
import LinearProgress from "./LinearProgress";

import { useSelector, useDispatch } from "react-redux";
import { CandidateProfile } from "../../../Redux/action/CandidateProfileAction";
import { VendorProfile } from "../../../Redux/action/VendorProfileAction";
import { ProfileProgressBarAction } from "../../../Redux/action/ProfileProgressBarAction";

import dummyDp from "../../../images/dummy_dp.png";
import videoThumb from "../../../images/video_img.jpg";
import Loader from "../../Common/Loader/loader";
import { toast } from "react-toastify";
import "./candidateDash.scss";
import { VisibilityOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// ---------------------- IMAGE CAROUSEL ------------------------
const ProjectCarousel = ({ images }) => {
  if (!images?.length) return null;

  const settings = {
    dots: true,
    infinite: true,
    slidesToShow: images.length > 1 ? 2 : 1,
    speed: 500,
  };

  return (
    <Slider {...settings}>
      {images.map((img, i) => (
        <Paper
          key={i}
          elevation={0}
          sx={{
            p: 1,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <img
            src={img}
            alt=""
            style={{
              height: 180,
              width: "90%",
              borderRadius: 8,
              objectFit: "cover",
            }}
          />
        </Paper>
      ))}
    </Slider>
  );
};

// ------------------- MAIN COMPONENT ------------------
const CandidateDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userResult } = useSelector((state) => state.Auth);
  const loading = useSelector((state) => state.Profile.loading);
  const userInfo = useSelector((state) => state.Profile.result);
  const profileProgress = useSelector((state) => state.ProgressBAR.result);

  const id = userResult?.userid;
  const candidateVendorId = userInfo?.vendorId ?? null;

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);

  useEffect(() => {
    dispatch(CandidateProfile(id));
    dispatch(ProfileProgressBarAction(id));
  }, [id]);

  useEffect(() => {
    if (candidateVendorId) dispatch(VendorProfile(candidateVendorId));
  }, [candidateVendorId]);

  const handleCopyLinkedIn = () => {
    if (!userInfo?.linkedIn) return toast.error("No LinkedIn profile found");

    navigator.clipboard.writeText(userInfo.linkedIn);
    toast.success("LinkedIn copied");
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loader />
      </Box>
    );
  }

  const statusBadge = userInfo?.emailVerified
    ? {
        label: "Verified",
        icon: <MdVerified color="#2b9348" />,
        sx: { background: "#d1fadf", color: "#1e7a40" },
      }
    : {
        label: "Unverified",
        icon: <RxCrossCircled color="red" />,
        sx: { background: "#ffe1e1", color: "#b91c1c" },
      };

  return (
    <>
      <Box sx={{ px: 3, py: 4 }}>
        <Grid container spacing={3}>
          {/* ---------- LEFT PROFILE SIDEBAR ---------- */}
          <Grid item xs={12} md={4} lg={3}>
            <Box
              sx={{
                position: { xs: "static", md: "sticky" },
                top: 20,
                zIndex: 5,
              }}
            >
              <Card
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 4,
                  textAlign: "center",
                  background: "linear-gradient(135deg, #EBF5FF, #FFFFFF)",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
                }}
              >
                <Avatar
                  src={userInfo?.profileImageUrl || dummyDp}
                  sx={{
                    width: 200,
                    height: 200,
                    mx: "auto",
                    mb: 2,
                    border: "4px solid #fff",
                    boxShadow: "0px 4px 15px rgba(0,0,0,0.1)",
                  }}
                />

                <Typography variant="h6" fontWeight={700}>
                  {userInfo?.firstName} {userInfo?.lastName}
                </Typography>

                <Chip
                  icon={statusBadge.icon}
                  label={statusBadge.label}
                  size="small"
                  sx={{ ...statusBadge.sx, mt: 1, fontWeight: 600 }}
                />

                <Divider sx={{ my: 3 }} />

                <Typography
                  fontWeight={700}
                  sx={{
                    textTransform: "uppercase",
                    textAlign: "left",
                    fontSize: "13px",
                    opacity: 0.9,
                    mb: 2,
                  }}
                >
                  Contact Information
                </Typography>

                <Box sx={{ display: "grid", gap: 2 }}>
                  {/* Email */}
                  <Tooltip arrow title={userInfo?.email}>
                    <Box
                      onClick={() => window.open(`mailto:${userInfo.email}`)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "pointer",
                        fontSize: "15px",
                        color: "#0E3F6F",
                        transition: "0.3s",
                        "&:hover": { color: "#286A94" },
                      }}
                    >
                      <MdOutlineMail size={20} color="#286a94" />
                      <Typography>
                        {userInfo?.email?.length > 30
                          ? userInfo.email.slice(0, 30) + "..."
                          : userInfo.email}
                      </Typography>
                    </Box>
                  </Tooltip>

                  {/* Phone */}
                  {userInfo?.mobile && (
                    <Tooltip arrow title="Call">
                      <Box
                        onClick={() =>
                          window.open(
                            `tel:+${userInfo?.dialCode}${userInfo.mobile}`
                          )
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          fontSize: "15px",
                          color: "#0E3F6F",
                          transition: "0.3s",
                          "&:hover": { color: "#286A94" },
                          gap: 1,
                        }}
                      >
                        <MdOutlineLocalPhone size={20} color="#286a94" />
                        <Typography>
                          +{userInfo?.dialCode} {userInfo.mobile}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}

                  {/* LinkedIn */}
                  {userInfo?.linkedIn && (
                    <Tooltip arrow title="Click to Copy URL">
                      <Box
                        onClick={handleCopyLinkedIn}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          fontSize: "15px",
                          color: "#0E3F6F",
                          transition: "0.3s",
                          "&:hover": { color: "#286A94" },
                          gap: 1,
                        }}
                      >
                        <AiOutlineLinkedin size={20} color="#286a94" />
                        <Typography>LinkedIn Profile</Typography>
                      </Box>
                    </Tooltip>
                  )}

                  {/* WhatsApp */}
                  {userInfo?.whatsapp && (
                    <Tooltip arrow title="Open WhatsApp Chat">
                      <Box
                        onClick={() =>
                          window.open(
                            `https://wa.me/${userInfo?.dialCode}${userInfo.whatsapp}`,
                            "_blank"
                          )
                        }
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          fontSize: "15px",
                          color: "#0E3F6F",
                          transition: "0.3s",
                          "&:hover": { color: "#286A94" },
                          gap: 1,
                        }}
                      >
                        <SiWhatsapp size={20} color="#25D366" />
                        <Typography>
                          +{userInfo?.dialCode} {userInfo.whatsapp}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Card>
            </Box>
          </Grid>

          {/* ------------ RIGHT SIDE CONTENT ------------- */}
          <Grid item xs={12} md={8} lg={9}>
            <Card
              sx={{
                p: 4,
                borderRadius: 4,
                boxShadow: "0px 8px 24px rgba(0,0,0,0.04)",
              }}
            >
              <Typography variant="h5" fontWeight={700}>
                {userInfo?.currentDesignation || "NA"}
              </Typography>

              <Typography
                sx={{ mt: 0.5, color: "#6b7280", textTransform: "uppercase" }}
              >
                Candidate ID:{" "}
                <strong style={{ color: "#0E3F6F" }}>
                  {userInfo?.candidateEmpId}
                </strong>
              </Typography>

              <Box sx={{ mt: 2 }}>
                <LinearProgress profileProgress={profileProgress} />
              </Box>

              {userInfo?.overview && (
                <Typography
                  sx={{
                    mt: 3,
                    lineHeight: 1.6,
                    fontSize: "15px",
                    color: "#475569",
                  }}
                >
                  {userInfo.overview}
                </Typography>
              )}

              <Divider sx={{ my: 4 }} />

              {/* SKILLS */}
              <Typography variant="h6" fontWeight={700} color={"#286a94"}>
                Skills
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, mt: 1 }}>
                {userInfo?.candidateSkills?.map((item, idx) => (
                  <Chip
                    key={idx}
                    label={item.skills}
                    sx={{
                      background: "#e9f2ff",
                      borderRadius: "999px",
                      px: 2,
                      py: 1,
                      fontWeight: 600,
                      fontSize: "13px",
                    }}
                  />
                ))}
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* ------- PROJECT ------- */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" fontWeight={700} color={"#286a94"}>
                  Projects
                </Typography>

                {(!userInfo?.candidateProjects ||
                  userInfo?.candidateProjects.length === 0) && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 4,
                      textAlign: "center",
                      border: "2px dashed #bcd3df",
                      borderRadius: 3,
                      background: "#f8fcff",
                    }}
                  >
                    <Typography
                      sx={{ mb: 2, fontSize: "16px", color: "#4b5563" }}
                    >
                      You haven’t added any projects yet.
                    </Typography>

                    <Button
                      variant="contained"
                      onClick={() => navigate("/show-candidate-projects")}
                      sx={{
                        background: "#286a94",
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        borderRadius: "30px",
                        textTransform: "none",
                        fontSize: "15px",
                        "&:hover": {
                          background: "#1f5575",
                        },
                      }}
                    >
                      ➕ Add Project
                    </Button>
                  </Box>
                )}

                {/* Show Projects If Exists */}
                {userInfo?.candidateProjects?.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ mt: 2 }}>
                      {userInfo.candidateProjects.map((proj, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            background: "#F9FAFB",
                            border: "1px solid #E5E7EB",
                            borderRadius: 3,
                            p: 3,
                            mb: 3,
                          }}
                        >
                          {/* Project Header */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <Chip
                              label={`#${idx + 1}`}
                              sx={{
                                background: "#286a94",
                                color: "white",
                                mr: 2,
                                fontWeight: 600,
                              }}
                            />
                            <Typography
                              sx={{ fontSize: "18px", fontWeight: 600 }}
                            >
                              {proj.projectName || "Untitled Project"}
                            </Typography>
                          </Box>

                          {/* Project Details */}
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                sx={{ fontWeight: 600, color: "#0E3F6F" }}
                              >
                                Role
                              </Typography>
                              <Typography sx={{ color: "#475569" }}>
                                {proj.role || "--"}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Typography
                                sx={{ fontWeight: 600, color: "#0E3F6F" }}
                              >
                                Duration
                              </Typography>
                              <Typography sx={{ color: "#475569" }}>
                                {proj.duration || "--"}
                              </Typography>
                            </Grid>

                            {/* 🔗 Clickable URL */}
                            <Grid item xs={12}>
                              <Typography
                                sx={{ fontWeight: 600, color: "#0E3F6F" }}
                              >
                                Project URL
                              </Typography>

                              {proj?.projectUrl ? (
                                <Typography
                                  component="a"
                                  href={
                                    proj.projectUrl.startsWith("http")
                                      ? proj.projectUrl
                                      : `https://${proj.projectUrl}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    fontSize: "15px",
                                    color: "#2563eb",
                                    wordBreak: "break-word",
                                    cursor: "pointer",
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  {proj.projectUrl}
                                </Typography>
                              ) : (
                                <Typography sx={{ color: "#475569" }}>
                                  --
                                </Typography>
                              )}
                            </Grid>

                            {/* Description */}
                            <Grid item xs={12}>
                              <Typography
                                sx={{ fontWeight: 600, color: "#0E3F6F" }}
                              >
                                Description
                              </Typography>
                              <Typography
                                sx={{
                                  color: "#475569",
                                  whiteSpace: "pre-line",
                                }}
                              >
                                {proj.description || "--"}
                              </Typography>
                            </Grid>

                            {/* Technologies */}
                            <Grid item xs={12}>
                              <Typography
                                sx={{ fontWeight: 600, color: "#0E3F6F" }}
                              >
                                Technologies
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  mt: 1,
                                }}
                              >
                                {proj?.technologies?.length > 0 ? (
                                  proj.technologies.map((tech, i) => (
                                    <Chip
                                      key={i}
                                      label={tech}
                                      sx={{
                                        background: "#eef6ff",
                                        fontWeight: 500,
                                      }}
                                    />
                                  ))
                                ) : (
                                  <Typography sx={{ color: "#475569" }}>
                                    --
                                  </Typography>
                                )}
                              </Box>
                            </Grid>
                          </Grid>

                          {/* Images */}
                          {proj?.projectImages?.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  mb: 1,
                                  fontSize: "14px",
                                  color: "#0E3F6F",
                                }}
                              >
                                Screenshots
                              </Typography>

                              <ProjectCarousel images={proj.projectImages} />
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>

                    {/* Add Another Project Button */}
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/show-candidate-projects")}
                      sx={{
                        borderColor: "#286a94",
                        color: "#286a94",
                        fontWeight: 600,
                        textTransform: "none",
                        mt: 2,
                        borderRadius: "30px",
                        px: 3,
                        "&:hover": {
                          background: "#286a94",
                          color: "white",
                        },
                      }}
                    >
                      ➕ Add More Projects
                    </Button>
                  </>
                )}
              </Box>
              <Divider sx={{ my: 3 }} />

              {/* ---------- EDUCATION ---------- */}
              <Box sx={{ mt: 5 }}>
                <Typography variant="h6" fontWeight={700} color={"#286a94"}>
                  Education
                </Typography>

                <Divider sx={{ my: 3 }} />

                {/* EMPTY STATE WITH ADD BUTTON */}
                {(!userInfo?.educations ||
                  userInfo?.educations?.length === 0) && (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: "center",
                      border: "2px dashed #bcd3df",
                      borderRadius: 3,
                      background: "#f8fcff",
                      color: "#475569",
                    }}
                  >
                    🎓 No education records found.
                    <Box sx={{ mt: 3 }}>
                      <button
                        onClick={() => navigate("/candidate-profile")}
                        style={{
                          background: "#286a94",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          border: "none",
                          cursor: "pointer",
                          color: "white",
                          fontWeight: 600,
                          transition: "0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.background = "#1d4c6b")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.background = "#286a94")
                        }
                      >
                        ➕ Add Education
                      </button>
                    </Box>
                  </Box>
                )}

                {/* LIST WHEN EDUCATION EXISTS */}
                {userInfo?.educations?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {userInfo.educations.map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          mb: 2.5,
                          background: "#F9FAFB",
                          border: "1px solid #E5E7EB",
                          transition: "0.25s",
                          "&:hover": { background: "#f1f8ff" },
                        }}
                      >
                        {/* Header */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <Chip
                            label={`#${idx + 1}`}
                            sx={{
                              background: "#286a94",
                              color: "white",
                              mr: 2,
                              fontWeight: 600,
                              px: 1,
                            }}
                          />
                          <Typography
                            sx={{ fontSize: "18px", fontWeight: 600 }}
                          >
                            {item?.degreeName || item?.type || "Education"}
                          </Typography>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* Details */}
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                textTransform: "uppercase",
                                color: "#0E3F6F",
                              }}
                            >
                              University / Board
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.institutionName ||
                                item?.board ||
                                item?.university ||
                                "--"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={3}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                textTransform: "uppercase",
                                color: "#0E3F6F",
                              }}
                            >
                              Passing Year
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.educationStatus === "PURSUING"
                                ? "Pursuing"
                                : item?.passingYear || "--"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={3}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                textTransform: "uppercase",
                                color: "#0E3F6F",
                              }}
                            >
                              Grade / Percentage
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.percentage ? `${item.percentage}%` : "--"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              <Divider sx={{ my: 3 }} />
              {/* ---------- CERTIFICATIONS ---------- */}
              <Box sx={{ mt: 5 }}>
                <Typography variant="h6" fontWeight={700} color={"#286a94"}>
                  Certifications
                </Typography>

                <Divider sx={{ my: 3 }} />

                {/* EMPTY STATE */}
                {(!userInfo?.candidateCertificate ||
                  userInfo?.candidateCertificate.length === 0) && (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: "center",
                      border: "2px dashed #bcd3df",
                      borderRadius: 3,
                      background: "#f8fcff",
                      color: "#475569",
                    }}
                  >
                    🎓 No certifications added yet.
                      <Box sx={{ mt: 3 }}>
                      <button
                        onClick={() => navigate("/candidate-profile")}
                        style={{
                          background: "#286a94",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          border: "none",
                          cursor: "pointer",
                          color: "white",
                          fontWeight: 600,
                          transition: "0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.background = "#1d4c6b")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.background = "#286a94")
                        }
                      >
                        ➕ Add Certificate
                      </button>
                    </Box>
                  </Box>
                )}

                {/* LIST OF CERTIFICATIONS */}
                {userInfo?.candidateCertificate?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {userInfo.candidateCertificate.map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          background: "#F9FAFB",
                          border: "1px solid #E5E7EB",
                          p: 2.5,
                          borderRadius: 3,
                          mb: 2.5,
                        }}
                      >
                        {/* Header */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <Chip
                            label={`#${idx + 1}`}
                            sx={{
                              background: "#286a94",
                              color: "white",
                              mr: 2,
                              fontWeight: 600,
                            }}
                          />
                          <Typography
                            sx={{ fontSize: "18px", fontWeight: 600 }}
                          >
                            {item?.courseName || "Untitled Certification"}
                          </Typography>
                        </Box>

                        {/* Details */}
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                color: "#0E3F6F",
                              }}
                            >
                              Issued By
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.courseProvider || "--"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                color: "#0E3F6F",
                              }}
                            >
                              Duration
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.duration || "--"}
                            </Typography>
                          </Grid>

                          {/* URL Preview */}
                          {item?.certificateImageUrl && (
                            <Grid item xs={12}>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  color: "#0E3F6F",
                                }}
                              >
                                Certificate
                              </Typography>

                              <Typography
                                component="a"
                                href={item?.certificateImageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  fontSize: "15px",
                                  color: "#2563eb",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 1,
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                📄 View Document
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* VIDEO MODAL */}
      <VideoModal
        showModal={showVideoModal}
        handleClose={() => setShowVideoModal(false)}
        title="Introduction"
        body={
          userInfo?.videoUrl ? (
            <video width="100%" height="300px" controls>
              <source src={userInfo.videoUrl} type="video/mp4" />
            </video>
          ) : (
            <img src={videoThumb} alt="No Video" />
          )
        }
      />
    </>
  );
};

export default CandidateDashboard;
